import * as THREE from 'three'
import { Renderer } from './Renderer'
import { CameraRig } from './Camera'
import { Loop } from './Loop'
import { Input } from './Input'
import { GameScene } from '../game/GameScene'
import { GameState } from '../game/GameState'
import { AudioManager } from '../audio/AudioManager'
import { economy } from '../services/economy'
import { slotOf } from '../data/cosmetics'
import { getIntro, getEnding, getTransition, CUTSCENES } from '../data/cutscenes'
import { getSettings } from '../services/settings'
import { UIRoot } from '../components/UIRoot'
import { FpsMeter } from '../components/FpsMeter'
import type { AppApi } from '../components/ui/app-api'
import { Direction, MatchStatus, Quality, ScreenName } from '../types'
import { diag, updateThreeDiagnostics } from '../utils/diagnostics'
import { probeDisplayRefreshHz } from './AdaptiveQuality'
import { COMPLETE_LEVEL_BUTTON_ENABLED } from '../config/release-flags'

/** Top-level conductor: owns renderer/scene/camera/loop and routes UI ↔ gameplay. */
export class GameApp implements AppApi {
  economy = economy
  audio = new AudioManager()

  private scene = new THREE.Scene()
  private cameraRig: CameraRig
  private renderer: Renderer
  private input = new Input()
  private gameState = new GameState()
  private gameScene: GameScene
  private ui: UIRoot
  private loop: Loop

  private screen: ScreenName = 'title'
  private paused = false
  private runSubmitted = false
  private inCutscene = false
  private introSeen = false
  private fpsMeter: FpsMeter | null = null

  constructor(canvas: HTMLCanvasElement, uiMount: HTMLElement) {
    const quality = getSettings().quality
    this.cameraRig = new CameraRig(window.innerWidth / window.innerHeight)
    this.renderer = new Renderer(canvas, this.scene, this.cameraRig.cam, quality)
    this.gameScene = new GameScene(this.scene, this.cameraRig, this.audio, this.gameState, quality)
    this.ui = new UIRoot(
      uiMount,
      this,
      (d) => this.onDir(d),
      () => this.togglePause(),
      COMPLETE_LEVEL_BUTTON_ENABLED ? () => this.completeLevelForDev() : undefined,
    )
    this.renderer.setAdaptiveQualityListener((state) => this.ui.cutscene.stage.setQualityTier(state.tier))

    this.input.attach(canvas)
    this.gameState.onHud((h) => this.ui.setHud(h))
    this.gameState.onStatus((s) => this.onStatus(s))

    // Story cutscenes: a transition plays before each new level; the ending plays after L10.
    this.gameScene.onAdvance = (next) => {
      const cs = getTransition(next)
      if (cs) this.playCutscene(cs, () => this.gameScene.advanceLevel())
      else this.gameScene.advanceLevel()
    }
    this.gameScene.onStoryComplete = () => {
      this.playCutscene(getEnding(), () => this.gameState.setStatus('gameover'))
    }
    // The shadow map is frozen for perf; re-render it once whenever a new maze is built.
    this.gameScene.onWorldRebuilt = () => this.renderer.refreshShadows()

    // Apply the saved render resolution before the first sizing pass (0 = native panel resolution).
    this.renderer.setResolution(getSettings().resolution)
    window.addEventListener('resize', () => this.onResize())
    this.onResize()
    if (window.matchMedia('(pointer: coarse)').matches) document.body.classList.add('is-touch')

    this.loop = new Loop(
      () => {},
      (dt) => this.frame(dt),
    )

    // Diagnostics surface for E2E.
    const d = diag()
    d.setScreen = (s) => this.go(s as ScreenName)
    d.startGame = () => this.startGame()
    d.pause = () => this.pauseGame()
    d.resume = () => this.resumeGame()
    d.freeze = () => {
      this.renderer.render() // commit one last frame, then stop
      this.loop.stop()
    }
    d.unfreeze = () => this.loop.start()
    d.capture = () => {
      this.renderer.render()
      return (this.renderer.renderer.domElement as HTMLCanvasElement).toDataURL('image/jpeg', 0.85)
    }
    if (import.meta.env.DEV) {
      // Dev/E2E helper ONLY — double-gated: the whole harness is installed only in DEV
      // (installDiagnostics), AND this command body is compiled out of prod. Lets a test
      // locally unlock + equip a cosmetic to assert its rendering without a real purchase.
      d.equip = (id) => {
        if (!economy.state.unlocked.includes(id)) economy.state.unlocked.push(id)
        const slot = slotOf(id)
        if (slot) economy.setEquipped(slot, id)
      }
      d.gotoLevel = (n) => {
        // jump into a running game at level n (rebuilds its maze + theme) for screenshots/tests
        if (this.screen !== 'play') this.startGame()
        this.gameScene.devGotoLevel(n)
      }
      d.playCutscene = (id) => {
        const cs = CUTSCENES[id]
        if (cs) this.playCutscene(cs, () => {})
      }
      // Dev-only on-screen FPS overlay (current / avg / worst-frame / ms). Hidden under test
      // automation so it never pollutes visual baselines; press ` (backquote) to toggle.
      this.fpsMeter = new FpsMeter()
      document.body.appendChild(this.fpsMeter.el)
      this.fpsMeter.setVisible(!navigator.webdriver)
      // Dev-only: expose the live scene + renderer so the standalone perf benchmark can isolate
      // overdraw (toggle object visibility) and inspect the GPU config. Never present in prod.
      ;(window as unknown as { __SCENE__: THREE.Scene }).__SCENE__ = this.scene
      ;(window as unknown as { __R__: Renderer }).__R__ = this.renderer
      // Dev perf-bisection hotkeys (the overlay legend is logged once so the keys are discoverable).
      // `  toggle overlay · B blur · 1 bloom · 2 shadows · 3 DPR cap · U uncap vsync
      console.info(
        '[perf] FPS overlay keys — `: show/hide · B: backdrop-blur · 1: bloom/post · 2: shadows · 3: DPR cap (2↔1) · U: uncap vsync',
      )
      window.addEventListener('keydown', (e) => {
        // Never hijack typing — a focused text field (e.g. the nickname input) must receive these
        // letters/digits normally (see FS-BB.7). Dev hotkeys only fire when nothing is being typed.
        const el = e.target as HTMLElement | null
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
        switch (e.code) {
          case 'Backquote':
            this.fpsMeter?.toggle()
            break
          case 'KeyB': {
            const off = document.body.classList.toggle('perf-no-blur')
            this.ui.toast(`backdrop blur: ${off ? 'OFF' : 'ON'}`)
            break
          }
          case 'Digit1':
            this.ui.toast(`bloom/post: ${this.renderer.togglePost() ? 'ON' : 'OFF'}`)
            break
          case 'Digit2':
            this.ui.toast(`shadows: ${this.renderer.toggleShadows() ? 'ON' : 'OFF'}`)
            break
          case 'Digit3':
            this.ui.toast(`DPR cap: ${this.renderer.cycleDprCap()}`)
            break
          case 'KeyU':
            this.ui.toast(`vsync: ${this.loop.setUncapped(!this.loop.uncapped) ? 'UNCAPPED' : 'on'}`)
            break
        }
      })
    }
    d.ready = true
    d.guiReady = true

    // Apply equipped cosmetics to BlueBob from the local cache now, then re-apply whenever
    // they change (a purchase auto-equips; the shop's equip toggle; or init's authoritative load).
    this.gameScene.setPlayerCosmetics(economy.getEquipped())
    economy.onChange(() => this.gameScene.setPlayerCosmetics(economy.getEquipped()))

    economy.init().finally(() => {
      this.ui.refreshCurrent()
      diag().econReady = true // signal init settled (E2E waits on this before equipping)
    })

    this.go('title')
    // Commit a complete first frame, then calibrate the physical display cadence while the game
    // loop is idle. This prevents a GPU-heavy first scene from being misidentified as a low-Hz panel.
    this.renderer.render()
    void probeDisplayRefreshHz(24)
      .then((refreshHz) => this.renderer.setDisplayRefreshRate(refreshHz))
      .catch(() => this.renderer.setDisplayRefreshRate(60))
      .finally(() => this.loop.start())
  }

  // ---------------- AppApi ----------------
  go(screen: ScreenName): void {
    this.screen = screen
    this.renderer.setOpeningVisualLock(screen !== 'play')
    this.paused = false
    this.ui.showPause(false)
    this.ui.setDevCompleteLevelEnabled(false)
    this.ui.showScreen(screen)
    if (screen !== 'play') this.cameraRig.mode = 'attract'
    diag().screen = screen
  }

  startGame(): void {
    this.audio.unlock()
    this.runSubmitted = false
    this.renderer.setOpeningVisualLock(false)
    this.ui.hideGameOver()
    this.screen = 'play'
    this.ui.showScreen('play')
    this.paused = false
    this.ui.showPause(false)
    this.gameScene.newGame()
    diag().screen = 'play'
  }

  restart(): void {
    this.ui.hideGameOver()
    this.startGame()
  }

  /** Menu PLAY entry: on the first play of the session, show the intro cutscene, then start. */
  playStory(): void {
    this.audio.unlock()
    if (this.introSeen) {
      this.startGame()
      return
    }
    this.introSeen = true
    this.playCutscene(getIntro(), () => this.startGame())
  }

  /** Show a cutscene over the game; the gameplay sim freezes until it ends, then `onDone` fires.
   * NON-REENTRANT: a request while a cutscene is already showing is ignored, so the active
   * cutscene's onDone (which clears `inCutscene`) is never dropped — without this guard a second
   * cutscene would strand `inCutscene = true` and silently freeze the simulation. */
  private playCutscene(cs: import('../data/cutscenes').Cutscene, onDone: () => void): void {
    if (this.inCutscene) return
    this.inCutscene = true
    this.audio.stopAmbient()
    this.ui.playCutscene(cs, () => {
      this.renderer.setSceneCamera(this.scene, this.cameraRig.cam)
      this.renderer.refreshShadows()
      this.inCutscene = false
      onDone()
      if (this.screen === 'play' && !this.paused && !this.inCutscene) this.audio.startAmbient()
    })
    this.ui.cutscene.resize(window.innerWidth / window.innerHeight)
    this.renderer.setSceneCamera(this.ui.cutscene.stage.scene, this.ui.cutscene.stage.camera)
  }

  resumeGame(): void {
    if (this.screen !== 'play') return
    this.paused = false
    this.ui.showPause(false)
    this.ui.setDevCompleteLevelEnabled(this.gameState.status === 'playing')
  }

  quitToMenu(): void {
    this.audio.stopAmbient()
    this.ui.hideGameOver()
    this.go('menu')
  }

  setQuality(q: Quality): void {
    this.renderer.applyQuality(q)
  }

  /** Fullscreen the whole page (canvas + DOM HUD/menus). Guarded so unsupported browsers
   * (e.g. iPhone Safari, which lacks element fullscreen) silently no-op rather than throw. */
  toggleFullscreen(): void {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.()?.catch(() => {})
    } else {
      void document.documentElement.requestFullscreen?.()?.catch(() => {})
    }
  }

  /** Apply the chosen render resolution (0 = native). Persistence is owned by the Settings screen,
   * mirroring setQuality; the renderer re-sizes its drawing buffer immediately. */
  setResolution(height: number): void {
    this.renderer.setResolution(height)
  }

  async submitRun(nickname?: string): Promise<void> {
    if (this.runSubmitted) return
    this.runSubmitted = true
    if (nickname !== undefined && nickname.trim()) this.economy.setNickname(nickname.trim())
    await this.economy.commitResult(this.gameState.score, this.gameState.level, this.gameState.jellyRun)
    if (!this.economy.state.online) this.ui.toast('Score saved locally — will sync when you reconnect.')
  }

  toast(msg: string): void {
    this.ui.toast(msg)
  }

  // ---------------- internal ----------------
  private onDir(d: Direction): void {
    // Ignore steering while a cutscene plays — otherwise a direction buffers into Input.desired
    // during the transition and snaps the player on the first frame after control returns.
    if (this.screen === 'play' && !this.paused && !this.inCutscene) {
      this.audio.unlock()
      this.input.setDesired(d)
    }
  }

  private togglePause(): void {
    if (this.screen !== 'play') return
    const st = this.gameState.status
    // Only pause from active play — not during the ready countdown or death animation.
    if (st === 'gameover' || st === 'levelclear' || st === 'ready' || st === 'dying') return
    this.paused = !this.paused
    this.ui.showPause(this.paused)
    this.ui.setDevCompleteLevelEnabled(!this.paused)
    if (this.paused) this.audio.play('click')
  }

  private pauseGame(): void {
    if (this.screen === 'play' && !this.paused) {
      this.paused = true
      this.ui.showPause(true)
      this.ui.setDevCompleteLevelEnabled(false)
    }
  }

  /** Temporary production QA convenience using GameScene's normal clear/reward/story boundary. */
  private completeLevelForDev(): void {
    if (this.screen !== 'play' || this.paused || this.inCutscene) return
    this.gameScene.devCompleteLevel()
  }

  private onStatus(s: MatchStatus): void {
    this.ui.setDevCompleteLevelEnabled(s === 'playing' && !this.paused && !this.inCutscene)
    switch (s) {
      case 'ready':
        this.ui.showBanner('READY!', 1400)
        break
      case 'playing':
        this.ui.hideBanner()
        break
      case 'dying':
        this.ui.showBanner('Ouch!', 900)
        break
      case 'levelclear':
        this.ui.showBanner(`Level ${this.gameState.level} Clear!`, 2000)
        break
      case 'gameover':
        this.onGameOver()
        break
    }
  }

  private onGameOver(): void {
    this.audio.stopAmbient()
    this.ui.showBanner('Game Over', 1300)
    const newBest = this.gameState.score >= this.gameState.hiScore && this.gameState.score > 0
    window.setTimeout(() => {
      this.ui.showGameOver({
        score: this.gameState.score,
        level: this.gameState.level,
        jelly: this.gameState.jellyRun,
        hiScore: this.gameState.hiScore,
        newBest,
      })
    }, 700)
  }

  private onResize(): void {
    const w = window.innerWidth
    const h = window.innerHeight
    this.cameraRig.setAspect(w / h)
    this.ui.cutscene.resize(w / h)
    this.renderer.resize(w, h)
  }

  private frame(dt: number): void {
    this.renderer.updateAdaptiveQuality(dt)
    this.input.update()
    diag().inputDir = this.input.desired
    if (this.input.consumePause()) this.togglePause()

    if (this.inCutscene) {
      this.ui.cutscene.update(dt)
    } else if (this.screen === 'play') {
      if (!this.paused && !this.inCutscene) {
        this.gameScene.setPlayerDesired(this.input.desired)
        this.gameScene.update(dt)
      }
      // paused / cutscene → frozen (still rendered below)
    } else {
      this.gameScene.idleUpdate(dt)
    }

    if (!this.inCutscene) {
      this.cameraRig.update(dt)
      // The opening attract camera moves every frame. Keep the gradient dome in
      // the camera's frame after that motion so the sky cannot drift or toggle.
      this.gameScene.environment.syncSkybox(this.cameraRig.cam)
    }
    this.renderer.render()
    // Dev/E2E only — production frames do no diagnostics work or allocation here (the harness +
    // overlay that consume these are themselves dev-only). renderer.render() is likewise self-gated.
    if (import.meta.env.DEV) {
      updateThreeDiagnostics({ fps: this.loop.fps })
      this.fpsMeter?.frame(dt)
    }
  }
}
